import { HashRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import './App.css';
import { Container, Sidebar, CustomProvider, Sidenav, Panel, Loader, Badge, IconButton, Whisper, Tooltip } from 'rsuite';
import { Icon } from '@rsuite/icons';
import { Nav } from 'rsuite';
import SettingIcon from '@rsuite/icons/Setting';
import PlusIcon from '@rsuite/icons/Plus';
import { FaMoneyBillTransfer } from "react-icons/fa6";
import { FaTasks } from "react-icons/fa";
import { TbBrain } from "react-icons/tb";
import Jobs from './Jobs';
import GPUInstances from './GPUInstances';
import SavedModels from './SavedModels';
import Inference from './Inference';
import Billing from './Billing';
import Signup from './Signup';
import Login from './Login';
import Landing from './Landing';
import UserIdSetup from './UserIdSetup';
import TokenDialog from './components/TokenDialog';
import SecretsDialog from './components/SecretsDialog';
import PaymentDialog from './components/PaymentDialog';
import { getBuildNumber, isProductionBuild } from './utils/buildInfo';
import { useAuth } from './utils/useAuth';
import { getApiUrl, getAuthHeaders, getUserId } from './utils/apiConfig';

const headerStyles = {
  padding: 20,
  fontSize: 16,
  background: '#34c3ff',
  color: '#fff'
};

function Page({ children }) {
  const buildNumber = getBuildNumber();
  const auth = useAuth();
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const [showSecretsDialog, setShowSecretsDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState(null);
  
  // Get user_id - don't render sidebar until user_id is confirmed
  const userId = getUserId(auth.user);

  const handleLogout = () => {
    if (auth.logout) {
      // Explicitly keep logout redirect on the current origin (dev vs prod)
      auth.logout({ logoutParams: { returnTo: window.location.origin } });
    }
  };

  const fetchBalance = async () => {
    if (!auth.hasAuth0 || auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently) {
      return;
    }
    
    // Don't fetch balance if user_id is not set
    if (!userId) {
      return;
    }
    
    setBalanceLoading(true);
    setBalanceError(null);
    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      const response = await fetch(getApiUrl('/api/users/me/balance'), {
        method: 'GET',
        headers: headers,
      });
      
      if (response.status === 503) {
        // Stripe not configured - this is OK
        setBalance(null);
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || 'Failed to fetch balance');
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        setBalance(data.balance);
      }
    } catch (error) {
      console.error('Failed to fetch balance:', error);
      setBalanceError(error.message);
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    if (auth.hasAuth0 && !auth.isLoading && auth.isAuthenticated && auth.getAccessTokenSilently && userId) {
      fetchBalance();
      // Refresh balance every 30 seconds
      const interval = setInterval(fetchBalance, 30000);
      return () => clearInterval(interval);
    }
  }, [auth.hasAuth0, auth.isLoading, auth.isAuthenticated, auth.getAccessTokenSilently, userId]);

  // Safety check: Don't render Page component if user_id is not set (should be caught by App.js, but extra safety)
  if (auth.hasAuth0 && auth.isAuthenticated && !userId) {
    return null; // App.js should have caught this, but return null as safety
  }

  return (
    <CustomProvider theme="dark">
      <Container>
        <Sidebar
          style={{ display: 'flex', flexDirection: 'column', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 1000 }}
          width={260}
          collapsible
        >
          <Sidenav defaultOpenKeys={['1']} appearance="subtle" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Sidenav.Header>
              <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={headerStyles}>
                  All Completed
                  {!isProductionBuild() && ` (ver #${buildNumber})`}
                </div>
              </Link>
            </Sidenav.Header>
            <Sidenav.Body style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Nav>
                <Nav.Item eventKey="jobs" icon={<Icon as={FaTasks} />} onSelect={() => {
                  window.location.hash = `#/jobs`;
                }}>
                  All Jobs
                </Nav.Item>
                <Nav.Item eventKey="gpu-instances" onSelect={() => {
                  window.location.hash = `#/gpu-instances`;
                }}>
                  GPU Instances
                </Nav.Item>
                <Nav.Item eventKey="models" onSelect={() => {
                  window.location.hash = `#/models`;
                }}>
                  Saved Models
                </Nav.Item>
                <Nav.Item eventKey="inference" icon={<Icon as={TbBrain} />} onSelect={() => {
                  window.location.hash = `#/inference`;
                }}>
                  Inference
                </Nav.Item>
                {/* <Nav.Item panel>
                  <Sidenav.GroupLabel>Billing</Sidenav.GroupLabel>
                </Nav.Item> */}
                <Nav.Menu title="Billing" icon={<Icon as={FaMoneyBillTransfer}/>}>
                  <Nav.Item eventKey="billing" onSelect={() => {
                    window.location.hash = `#/billing`;
                  }}>
                    Transactions
                  </Nav.Item>
                  <Nav.Item onSelect={() => setShowPaymentDialog(true)}>
                    Add Funds
                  </Nav.Item>
                </Nav.Menu>
                <Nav.Item divider />
                {auth.hasAuth0 && auth.isAuthenticated && (
                  <>
                    {/* <Nav.Item panel>
                      <Sidenav.GroupLabel>Settings</Sidenav.GroupLabel>
                    </Nav.Item> */}
                    <Nav.Menu title="Settings" icon={<SettingIcon />}>
                      <Nav.Item onSelect={() => setShowTokenDialog(true)}>
                        Manage API Token
                      </Nav.Item>
                      <Nav.Item onSelect={() => setShowSecretsDialog(true)}>
                        Manage User Secrets
                      </Nav.Item>
                      <Nav.Item onSelect={handleLogout}>
                        Logout
                      </Nav.Item>
                    </Nav.Menu>
                  </>
                )}
              </Nav>
              {auth.hasAuth0 && auth.isAuthenticated && userId && (
                <div style={{ padding: '20px', marginTop: 'auto' }}>
                  <Whisper 
                    placement="top" 
                    trigger="hover" 
                    speaker={<Tooltip>{auth.user?.email || 'No email'}</Tooltip>}
                  >
                    <div style={{ marginBottom: '10px', fontSize: '12px', color: '#999', cursor: 'help' }}>
                      {userId || auth.user?.email || auth.user?.name || 'User'}
                    </div>
                  </Whisper>
                  {balance !== null && (
                    <div style={{ marginBottom: '15px', padding: '10px', background: '#2a2a2a', borderRadius: '4px', position: 'relative' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontSize: '11px', color: '#999', marginBottom: '5px' }}>Balance</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: balance < 0 ? '#ff4d4f' : '#52c41a' }}>
                            ${balance.toFixed(2)}
                            {balance < 0 && (
                              <Badge content="Low" color="red" style={{ marginLeft: '8px' }} />
                            )}
                          </div>
                          {balance < 0 && (
                            <div style={{ fontSize: '10px', color: '#ff4d4f', marginTop: '5px' }}>
                              Add funds to submit jobs
                            </div>
                          )}
                        </div>
                        <IconButton
                          circle
                          icon={<PlusIcon />}
                          onClick={() => setShowPaymentDialog(true)}
                          color="green"
                          appearance="subtle"
                          title="Add Funds"
                        />
                      </div>
                    </div>
                  )}
                  {balanceError && (
                    <div style={{ marginBottom: '15px', fontSize: '11px', color: '#999' }}>
                      Balance unavailable
                    </div>
                  )}
                  <TokenDialog 
                    show={showTokenDialog} 
                    onClose={() => setShowTokenDialog(false)} 
                  />
                  <SecretsDialog 
                    show={showSecretsDialog} 
                    onClose={() => setShowSecretsDialog(false)} 
                  />
                  <PaymentDialog 
                    show={showPaymentDialog} 
                    onClose={() => setShowPaymentDialog(false)}
                    onPaymentSuccess={(amount) => {
                      // Refresh balance after successful payment
                      fetchBalance();
                    }}
                  />
                </div>
              )}
            </Sidenav.Body>
          </Sidenav>
        </Sidebar>
        <Container style={{ marginLeft: 260 }}>
          {children}
        </Container>
      </Container>
    </CustomProvider>
  );
}


export default function App() {
  const auth = useAuth();
  const [userMetadata, setUserMetadata] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState(null);

  // Fetch user metadata from backend API when authenticated
  // This ensures we get the actual user_metadata from Auth0 Management API
  useEffect(() => {
    // Only fetch if authenticated and not already loading
    // Allow retry if metadata is empty object (failed fetch) or if there was an error
    const shouldFetch = auth.hasAuth0 && 
                       auth.isAuthenticated && 
                       auth.getAccessTokenSilently && 
                       !metadataLoading &&
                       (!userMetadata || (typeof userMetadata === 'object' && Object.keys(userMetadata).length === 0));
    
    if (shouldFetch) {
      setMetadataLoading(true);
      setMetadataError(null);
      
      getAuthHeaders(auth.getAccessTokenSilently)
        .then(headers => {
          return fetch(getApiUrl('/api/users/me/api-key'), {
            method: 'GET',
            headers: headers,
          });
        })
        .then(async response => {
          if (!response.ok) {
            // If 401, user might not be fully authenticated yet
            if (response.status === 401) {
              throw new Error('Not authenticated');
            }
            // If 428, user needs to set user_id - this is expected, not an error
            if (response.status === 428) {
              // Set empty metadata to trigger UserIdSetup modal
              setUserMetadata({});
              setMetadataError(null); // Don't treat this as an error
              return { status: 'success', metadata: {} };
            }
            // Try to parse error response, but handle cases where response might not be JSON
            try {
              const data = await response.json();
              const errorMsg = data.detail?.message || data.detail?.error || data.detail || `Failed to fetch metadata (${response.status})`;
              throw new Error(errorMsg);
            } catch (parseError) {
              // If JSON parsing fails, use status text
              throw new Error(`Failed to fetch metadata: ${response.statusText || response.status}`);
            }
          }
          return response.json();
        })
        .then(data => {
          if (data.status === 'success' && data.metadata) {
            setUserMetadata(data.metadata);
            setMetadataError(null); // Clear any previous errors
          } else {
            // If no metadata returned, set empty object but don't set error
            // This allows fallback to Auth0 user object
            setUserMetadata({});
          }
        })
        .catch(err => {
          console.warn('❌ Failed to fetch user metadata:', err);
          // Don't set error for 401 - might be transient during auth
          if (err.message !== 'Not authenticated') {
            setMetadataError(err.message);
          }
          // Set empty object to allow fallback check
          setUserMetadata({});
        })
        .finally(() => {
          setMetadataLoading(false);
        });
    }
  }, [auth.hasAuth0, auth.isAuthenticated, auth.getAccessTokenSilently]);

  // Show loading state while Auth0 is initializing or fetching user metadata
  if (auth.hasAuth0 && (auth.isLoading || (auth.isAuthenticated && metadataLoading && !userMetadata))) {
    return (
      <CustomProvider theme="dark">
        <Container style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Loader size="lg" content="Loading..." />
        </Container>
      </CustomProvider>
    );
  }

  // Show main landing page if Auth0 is configured but user is not authenticated
  // But allow access to signup and login pages
  if (auth.hasAuth0 && !auth.isAuthenticated && !auth.isLoading) {
    return (
      <Router>
        <Routes>
          <Route path="/signup" element={<Signup />} />
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Landing />} />
        </Routes>
      </Router>
    );
  }

  // Check if authenticated user has user_id - REQUIRED
  // Block all access until user_id is set (for both email/password and OAuth users)
  // user_id is stored in user_metadata for all users
  // Fetch metadata from backend API to ensure we get the latest data from Auth0 Management API
  // This check MUST happen before rendering any routes or Page components
  if (auth.hasAuth0 && auth.isAuthenticated) {
    // Wait for metadata fetch to complete before checking
    // If still loading, show loading state (handled above)
    if (metadataLoading) {
      // Already handled by loading check above
      return null;
    }
    
    // If metadata fetch failed with an error (like 500), don't trust fallback
    // Show UserIdSetup to force proper user_id setup
    if (metadataError && metadataError !== 'Not authenticated') {
      return <UserIdSetup />;
    }
    
    // If we have metadata from API, use it (backend returns environment-specific metadata)
    // Otherwise, try to get from Auth0 user object as fallback (only if no error)
    // Use getUserId helper which correctly accesses user_metadata.{env}.user_id
    let userId = null;
    
    // Check if userMetadata is a non-empty object with user_id
    // Backend get_metadata returns environment-specific metadata, so user_id is directly accessible
    if (userMetadata && typeof userMetadata === 'object' && Object.keys(userMetadata).length > 0) {
      userId = userMetadata.user_id;
    } else if (!metadataError && auth.user) {
      // Fallback: use getUserId helper to get from Auth0 user object
      // getUserId correctly accesses user_metadata.{env}.user_id based on environment
      // Only use fallback if metadata fetch didn't fail with an error
      userId = getUserId(auth.user);
    }
    
    // If user_id is missing, BLOCK access and show setup form
    // This is required - users cannot proceed without setting user_id
    // Do NOT render any routes or Page components until user_id is set
    if (!userId || !userId.trim()) {
      return <UserIdSetup />;
    }
  }

  // Only render app content (Router, Routes, Page components) after user_id is confirmed
  // Show app content if authenticated with user_id or if Auth0 is not configured
  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <Page>
            <Jobs />
          </Page>
        } />
        <Route path="/jobs" element={
          <Page>
            <Jobs />
          </Page>
        } />
        <Route path="/gpu-instances" element={
          <Page>
            <GPUInstances />
          </Page>
        } />
        <Route path="/models" element={
          <Page>
            <SavedModels />
          </Page>
        } />
        <Route path="/inference" element={
          <Page>
            <Inference />
          </Page>
        } />
        <Route path="/billing" element={
          <Page>
            <Billing />
          </Page>
        } />
      </Routes>
    </Router>
  );
}

